import { motion } from "motion/react";
import { Download, TrendingUp, Users, Target, FileText } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

export function Reports() {
  const annualReports = [
    { year: "2025-2026", pages: 48, downloads: 1243 },
    { year: "2024-2025", pages: 42, downloads: 2156 },
    { year: "2023-2024", pages: 36, downloads: 1892 },
    { year: "2022-2023", pages: 28, downloads: 1456 },
  ];

  const fundingData = [
    { year: "2023", raised: 0.8, distributed: 0.75 },
    { year: "2024", raised: 1.5, distributed: 1.4 },
    { year: "2025", raised: 2.3, distributed: 2.1 },
    { year: "2026", raised: 2.8, distributed: 2.5 },
  ];

  const categoryData = [
    { name: "Tourism", value: 35, color: "#2D5F4C" },
    { name: "Agriculture", value: 25, color: "#8B9D83" },
    { name: "Education", value: 20, color: "#D4B896" },
    { name: "Infrastructure", value: 15, color: "#E8E4DE" },
    { name: "Women Empowerment", value: 5, color: "#6B7280" },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-32 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-7xl mb-6">Reports & Impact</h1>
            <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
              Transparency is at the heart of everything we do. Track our impact
              through detailed reports and data.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Impact Metrics */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h2 className="text-4xl mb-12 text-center">Impact Dashboard</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <Card>
              <CardContent className="p-6 text-center">
                <Target className="mx-auto mb-3 text-primary" size={32} />
                <div className="text-3xl text-primary mb-2">47</div>
                <div className="text-sm text-muted-foreground">
                  Projects Funded
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <Users className="mx-auto mb-3 text-primary" size={32} />
                <div className="text-3xl text-primary mb-2">15,000+</div>
                <div className="text-sm text-muted-foreground">
                  Lives Impacted
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <TrendingUp className="mx-auto mb-3 text-primary" size={32} />
                <div className="text-3xl text-primary mb-2">₹2.8M</div>
                <div className="text-sm text-muted-foreground">
                  Funds Distributed
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <FileText className="mx-auto mb-3 text-primary" size={32} />
                <div className="text-3xl text-primary mb-2">32</div>
                <div className="text-sm text-muted-foreground">
                  Partner NGOs
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* Funding Chart */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <Card>
            <CardContent className="p-8">
              <h2 className="text-3xl mb-8">Funds Raised vs Distributed</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={fundingData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis label={{ value: "Amount (₹M)", angle: -90, position: "insideLeft" }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="raised" name="Raised" fill="#2D5F4C" />
                  <Bar dataKey="distributed" name="Distributed" fill="#8B9D83" />
                </BarChart>
              </ResponsiveContainer>
              <p className="text-sm text-muted-foreground mt-6 text-center">
                Over 95% of funds raised are distributed to projects. The remaining
                covers due diligence and monitoring costs.
              </p>
            </CardContent>
          </Card>
        </motion.section>

        {/* Category Distribution */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-8">
                <h2 className="text-3xl mb-8">Funding by Category</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(((percent ?? 0) * 100).toFixed(0))}%`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-8">
                <h2 className="text-3xl mb-8">Key Achievements (2026)</h2>
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      ✓
                    </div>
                    <div>
                      <div className="mb-1">15 Projects Completed</div>
                      <div className="text-sm text-muted-foreground">
                        All projects met or exceeded impact goals
                      </div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      ✓
                    </div>
                    <div>
                      <div className="mb-1">8,500 Direct Beneficiaries</div>
                      <div className="text-sm text-muted-foreground">
                        New livelihoods created this year
                      </div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      ✓
                    </div>
                    <div>
                      <div className="mb-1">UNWTO Recognition</div>
                      <div className="text-sm text-muted-foreground">
                        Certified for sustainable tourism development
                      </div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      ✓
                    </div>
                    <div>
                      <div className="mb-1">12 New Partner NGOs</div>
                      <div className="text-sm text-muted-foreground">
                        Expanding our network of verified organizations
                      </div>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </motion.section>

        {/* Annual Reports */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl mb-8 text-center">Annual Reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {annualReports.map((report, index) => (
              <motion.div
                key={report.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FileText className="text-primary" size={32} />
                    </div>
                    <h3 className="text-xl text-center mb-2">{report.year}</h3>
                    <p className="text-sm text-muted-foreground text-center mb-4">
                      {report.pages} pages • {report.downloads} downloads
                    </p>
                    <Button variant="outline" className="w-full">
                      <Download className="mr-2" size={18} />
                      Download PDF
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
}
